'use client';

import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import {
  Camera,
  CheckSquare,
  Coins,
  GraduationCap,
  Home,
  Map,
  Phone,
  Star,
  TrafficCone,
} from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { useSession } from 'next-auth/react';
import { NavbarMobile } from './NavbarMobile.client';
import { ProfileArea } from './ProfileArea.client';
import { ReporterLabel } from './ReporterLabel';
import { FinanceHoverMenu } from './FinanceHoverMenu.client';

type Tab = {
  id: number;
  label: string;
  href: string;
  Icon: LucideIcon;
  kind?: 'reporter' | 'default';
};

const tabs: Tab[] = [
  { id: 1, label: 'Погода', href: '/', Icon: Home },
  { id: 2, label: 'Пробки', href: '/traffic', Icon: TrafficCone },
  { id: 3, label: 'Я Томск', href: '/reporter', Icon: Camera, kind: 'reporter' },
  { id: 4, label: 'Личный помощник', href: '/todos', Icon: CheckSquare },
  { id: 5, label: 'Гид по Томску', href: '/tourism', Icon: Map },
  { id: 6, label: 'Гороскоп', href: '/horoscope/daily/common', Icon: Star },
  { id: 8, label: 'Финансы', href: '/finance', Icon: Coins },
  { id: 10, label: 'Студент Томск', href: '/students', Icon: GraduationCap },
  { id: 9, label: 'Контакты', href: '/contacts', Icon: Phone },
];

const financeLinks = [
  { name: 'Курс валют ЦБ РФ', href: '/finance' },
  { name: 'График Валют', href: '/finance/currency-chart' },
  { name: 'Конвертер валют', href: '/finance/converter' },
  { name: 'Курсы криптовалют', href: '/finance/crypto' },
  { name: 'Новости', href: '/finance/news' },
];

function isActive(pathname: string, href: string) {
  if (href === '/') return pathname === '/';
  return pathname.startsWith(href);
}

export function NavbarClient() {
  const { data: session } = useSession();
  const pathname = usePathname();

  return (
    <div className="flex justify-between items-center mb-2">
      <nav
        className="hidden md:flex flex-wrap justify-center gap-2 text-white font-medium transition-all duration-300 flex-1 overflow-visible"
        role="navigation"
        aria-label="Главное меню"
      >
        {tabs.map((tab) =>
          tab.href === '/finance' ? (
            <FinanceHoverMenu
              key={tab.id}
              tab={tab}
              financeLinks={financeLinks}
              pathname={pathname}
              active={isActive(pathname, '/finance')}
            />
          ) : (
            <Link
              key={tab.id}
              href={tab.href}
              prefetch={false}
              aria-current={isActive(pathname, tab.href) ? 'page' : undefined}
              className={clsx(
                'flex items-center gap-1 px-3 py-1.5 rounded-xl hover:bg-white/20',
                isActive(pathname, tab.href) && 'bg-white/30 dark:text-sky-400 font-semibold',
              )}
            >
              <tab.Icon size={18} />
              <span>{tab.kind === 'reporter' ? <ReporterLabel /> : tab.label}</span>
            </Link>
          ),
        )}
      </nav>

      <div className="hidden md:flex items-center gap-4">
        <ProfileArea session={session ?? null} />
      </div>

      <div className="md:hidden flex justify-between items-center w-full mb-2">
        <NavbarMobile
          tabs={tabs}
          financeLinks={financeLinks}
          pathname={pathname}
          isAdmin={session?.user?.role === 'ADMIN'}
          session={session ?? null}
        />
      </div>
    </div>
  );
}
